import { z } from 'zod';

import { RawUIFolderSchema } from './raw-ui-folder';
import {
  allowVariablePlaceholders,
  variablePlaceholderSchema,
} from './raw-variable-placeholder';

// NOTE(mc, 2025-04-01): ReadonlyDeep/SimplifyDeep get confused by recursive types here
// so using a shallow Readonly instead. Please use with caution to avoid unexpected mutation.
export type RawTriggerConfig = Readonly<z.infer<typeof RawTriggerConfigSchema>>;

export type RawTriggerEvent = Readonly<z.infer<typeof RawTriggerEventSchema>>;

export type RawTriggerEval = Readonly<z.infer<typeof RawTriggerEvalSchema>>;

export type EvalValue =
  | number
  | string
  | boolean
  | readonly EvalValue[]
  | { readonly [key: string]: EvalValue };

export const EvalValueSchema: z.ZodType<EvalValue> = z.union([
  z.number(),
  z.string(),
  z.boolean(),
  z.array(z.lazy(() => EvalValueSchema)),
  z.record(
    z.string(),
    z.lazy(() => EvalValueSchema)
  ),
]);

const createRawTriggerEvalSchema = (allowVariables = false) => {
  return allowVariablePlaceholders(
    z.object({
      operator: z.string().optional(),
      value: EvalValueSchema.optional(),
    }),
    allowVariables
  ).passthrough();
};
export const RawTriggerEvalSchema = createRawTriggerEvalSchema();

const createRawTriggerConditionalSchema = (allowVariables = false) => {
  return allowVariablePlaceholders(
    z.object({
      data_capture_method: z.string().optional(),
      condition: z
        .object({
          evals: z.array(createRawTriggerEvalSchema(allowVariables)).optional(),
        })
        .passthrough()
        .optional(),
    }),
    allowVariables
  ).passthrough();
};

const createRawTriggerEventSchema = (allowVariables = false) => {
  return allowVariablePlaceholders(
    z.object({
      type: z.string(),
      data_ingested: allowVariablePlaceholders(
        z.object({
          data_types: z
            .array(
              allowVariables
                ? z.string().or(variablePlaceholderSchema)
                : z.string()
            )
            .optional(),
        }),
        allowVariables
      )
        .passthrough()
        .optional(),
      conditional: createRawTriggerConditionalSchema(allowVariables).optional(),
      log_levels: z
        .array(
          allowVariables ? z.string().or(variablePlaceholderSchema) : z.string()
        )
        .optional(),
    }),
    allowVariables
  ).passthrough();
};
export const RawTriggerEventSchema = createRawTriggerEventSchema();

const createRawTriggerConfigNotificationSchema = (allowVariables = false) => {
  return allowVariablePlaceholders(
    z.object({
      type: z
        .union([
          z.literal('webhook'),
          z.literal('email'),
          z.literal('push'),
          z.literal('do_command'),
        ])
        .optional(),
      value: z.string().optional(),
      command: z.record(z.string(), z.unknown()).optional(),
      application: z.string().optional(),
      seconds_between_notifications: z.number().optional(),
      auth: z
        .object({
          basic: z
            .object({
              username: z.string().optional(),
              password: z.string().optional(),
            })
            .passthrough()
            .optional(),
        })
        .passthrough()
        .optional(),
    }),
    allowVariables
  ).passthrough();
};
export const RawTriggerConfigNotificationSchema =
  createRawTriggerConfigNotificationSchema();

export const createRawTriggerConfigSchema = (allowVariables = false) => {
  return allowVariablePlaceholders(
    z.object({
      name: z.string().default(''),
      event: createRawTriggerEventSchema(allowVariables).optional(),
      notifications: createRawTriggerConfigNotificationSchema(allowVariables)
        .array()
        .optional(),
      disabled: z.boolean().optional(),
      notes: z.string().optional(),
      ui_folder: RawUIFolderSchema.optional(),
    }),
    allowVariables
  ).passthrough();
};

export const RawTriggerConfigSchema = createRawTriggerConfigSchema();
